import React, { useMemo } from 'react';
import { Button } from '../../../components/ui';
import { Modal, ModalContent, ModalFooter, ModalHeader } from '../../../components/ui/Modal';
import { EntityAttachmentsSection } from '../../../components/EntityAttachmentsSection';
import { EntityImageGallery } from '../../../components/EntityImageGallery';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import api from '../../../services/api';
import { useSpareItem, type SpareItemAttachment } from './useData';

export type SpareItemDetailModalProps = {
  itemId: number | null;
  isOpen: boolean;
  onClose: () => void;
  zIndex?: number;
};

const FlagChip: React.FC<{ label: string; tone: 'ok' | 'warn' | 'danger' | 'muted' }> = ({ label, tone }) => {
  const { colors } = useTheme();
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
        tone === 'ok' && colors.success,
        tone === 'warn' && colors.warning,
        tone === 'danger' && colors.error,
        tone === 'muted' && cn(colors.surfaceHover, colors.textMuted),
      )}
    >
      {label}
    </span>
  );
};

const MetaCell: React.FC<{ label: string; value: React.ReactNode; className?: string }> = ({
  label,
  value,
  className,
}) => {
  const { colors } = useTheme();
  return (
    <div className={cn('min-w-0', className)}>
      <div className={cn('text-[10px] font-semibold uppercase tracking-wider', colors.textMuted)}>{label}</div>
      <div className={cn('mt-1 break-words text-sm font-medium tabular-nums', colors.text)}>{value || ''}</div>
    </div>
  );
};

export const SpareItemDetailModal: React.FC<SpareItemDetailModalProps> = ({
  itemId,
  isOpen,
  onClose,
  zIndex = 120,
}) => {
  const { colors } = useTheme();
  const id = isOpen && itemId != null && itemId > 0 ? itemId : 0;
  const { data: item, isLoading, isError } = useSpareItem(id);

  const files: SpareItemAttachment[] = useMemo(
    () => item?.FileAttachment ?? item?.attachments ?? [],
    [item],
  );
  const imageFiles = useMemo(
    () => files.filter((f) => (f.fileType || f.mimeType || '').toLowerCase().startsWith('image/')),
    [files],
  );

  const folderPath = useMemo(() => {
    if (!item) return '';
    return [item.library?.name, item.category?.name].map((s) => (s ?? '').trim()).filter(Boolean).join(' › ');
  }, [item]);

  const fetchImage = async (fileId: number) => {
    if (!item) return '';
    const response = await api.get(`/spare-items/${item.id}/files/${fileId}`, { responseType: 'blob' });
    return URL.createObjectURL(response.data);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" zIndex={zIndex}>
      <ModalHeader>
        <div className="flex w-full items-start justify-between gap-3">
          <div className="min-w-0">
            <p className={cn('text-[11px] font-semibold uppercase tracking-wider', colors.textMuted)}>
              Spare item
            </p>
            <h2 className={cn('mt-0.5 truncate text-lg font-semibold', colors.text)}>
              {isLoading ? 'Loading…' : item?.name || (isError ? 'Item unavailable' : 'Item details')}
            </h2>
            {item?.vendorCode ? (
              <p className={cn('mt-1 truncate font-mono text-xs tabular-nums', colors.textMuted)}>
                {item.vendorCode}
              </p>
            ) : null}
          </div>
          <Button type="button" variant="ghost" size="icon" title="Close" aria-label="Close" onClick={onClose}>
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </Button>
        </div>
      </ModalHeader>

      <ModalContent className="max-h-[min(72vh,40rem)] space-y-5 !pt-4">
        {isLoading ? (
          <div className={cn('flex flex-col items-center justify-center gap-2 py-16 text-sm', colors.textMuted)}>
            <div className={cn('h-5 w-5 animate-spin rounded-full border-2 border-t-transparent', colors.border)} />
            Loading item…
          </div>
        ) : isError || !item ? (
          <div
            className={cn(
              'rounded-xl border border-dashed px-6 py-14 text-center text-sm',
              colors.border,
              colors.surfaceHover,
              colors.textMuted,
            )}
          >
            Could not load this spare item.
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <FlagChip label={item.isActive ? 'Active' : 'Inactive'} tone={item.isActive ? 'ok' : 'muted'} />
              {item.isCritical ? <FlagChip label="Critical" tone="warn" /> : null}
              {item.isHazardousMaterial ? <FlagChip label="Hazardous" tone="danger" /> : null}
              {folderPath ? (
                <span
                  className={cn(
                    'inline-flex max-w-full truncate rounded-md border px-2 py-0.5 text-xs',
                    colors.border,
                    colors.textMuted,
                  )}
                  title={folderPath}
                >
                  {folderPath}
                </span>
              ) : null}
            </div>

            <div className={cn('grid grid-cols-2 gap-4 rounded-xl border p-4 sm:grid-cols-4', colors.border, colors.surfaceHover)}>
              <MetaCell label="Drawing" value={item.drawingNo} />
              <MetaCell label="Serial" value={item.serialNo} />
              <MetaCell
                label="Stock min"
                value={item.stockMin != null ? String(item.stockMin) : null}
              />
              <MetaCell
                label="Stock max"
                value={item.stockMax != null ? String(item.stockMax) : null}
              />
            </div>

            {item.notes?.trim() ? (
              <div className={cn('rounded-xl border p-4', colors.border)}>
                <div className={cn('text-[10px] font-semibold uppercase tracking-wider', colors.textMuted)}>
                  Notes
                </div>
                <p className={cn('mt-2 whitespace-pre-wrap text-sm leading-relaxed', colors.text)}>{item.notes}</p>
              </div>
            ) : null}

            {imageFiles.length > 0 ? (
              <div>
                <div className={cn('mb-2 text-[10px] font-semibold uppercase tracking-wider', colors.textMuted)}>
                  Photos ({imageFiles.length})
                </div>
                <EntityImageGallery
                  files={imageFiles.map((f) => ({
                    id: f.id,
                    fileName: f.fileName,
                    fileType: f.fileType || f.mimeType,
                    fileSize: f.fileSize,
                    uploadedAt: f.uploadedAt,
                  }))}
                  getImageUrl={(fileId) => `/spare-items/${item.id}/files/${fileId}`}
                  fetchImage={fetchImage}
                />
              </div>
            ) : null}

            {files.length > 0 ? (
              <div>
                <div className={cn('mb-2 text-[10px] font-semibold uppercase tracking-wider', colors.textMuted)}>
                  Files ({files.length})
                </div>
                <EntityAttachmentsSection
                  downloadEndpoint=""
                  showBulkDownload={false}
                  files={files.map((f) => ({
                    id: f.id,
                    fileName: f.fileName,
                    fileSize: f.fileSize ?? null,
                    fileType: f.fileType || f.mimeType || null,
                    uploadedAt: f.uploadedAt ?? null,
                  }))}
                  getViewFileUrl={(fileId) => `/spare-items/${item.id}/files/${fileId}`}
                />
              </div>
            ) : null}
          </>
        )}
      </ModalContent>

      <ModalFooter>
        <Button type="button" variant="default" size="sm" onClick={onClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};
